<template>
  <div class="quiz">
    <div v-if="currentQuiz && !isLoading" class="quiz__modal">
      <div class="quiz__wrapper">
        <div class="quiz__titlebar">
          <TitleBar
            v-if="!hasStarted && !isLoadingModal"
            :title="title"
            :currentPosition="-1"
            :totalQuestions="questionCount"
            :isQuizComplete="true"
            :hasStarted="false"
            @closeModal="closeModal()"
          />
        </div>
        <div v-if="!isLoadingModal" class="quiz__content-wrapper">
          <div class="quiz__content-left">
            <img class="quiz__rocket" src="@/assets/images/INE_rocket.png" />
          </div>
          <div class="quiz__content-right">
            <div>
              <h3 class="quiz__motivator">Let's Do This!</h3>
              <div class="quiz__count-wrapper">
                <p class="quiz__count-holder">
                  <span class="quiz__count">{{ questionCount }}</span>
                </p>
                <p class="quiz__count-label">Questions</p>
              </div>
              <div class="quiz__count-wrapper">
                <p class="quiz__count-holder">
                  <span class="quiz__count">{{ previousAttemptCount }}</span>
                </p>
                <p class="quiz__count-label">Attempts</p>
              </div>
            </div>
            <div class="quiz__btn-wrapper">
              <button
                @click="openModal()"
                class="quiz__take-quiz-btn btn__large btn__large--alternate"
              >
                Start Quiz
              </button>
            </div>
          </div>
        </div>
        <div v-else class="quiz-modal__loader-wrapper">
          <BarLoader
            class="quiz-modal__loader"
            color="#ef7d22"
            :size="5"
            sizeUnit="rem"
          />
        </div>
      </div>
    </div>
    <div class="quiz__modal-wrapper">
      <QuizModal
        v-if="isModalShown"
        :quiz="currentQuiz"
        :videoId="videoId"
        :attempt="quizAttempt"
        :title="title"
        :nextVideo="nextVideo"
        :finishedQuiz="finishedQuiz"
        @closeModal="closeModal"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { BarLoader } from '@saeris/vue-spinners'
import TitleBar from '@/components/course/content/level/quiz-modal/TitleBar.vue'
import QuizModal from '@/components/course/content/level/QuizModal.vue'

export default {
  name: 'Quiz',
  components: {
    TitleBar,
    QuizModal,
    BarLoader,
  },
  data() {
    return {
      isModalShown: false,
      isLoading: true,
      isLoadingModal: false,
      hasStarted: false,
    }
  },
  computed: {
    ...mapGetters({
      currentQuiz: 'quizzes/currentQuiz',
      previousAttempt: 'quizzes/previousAttempt',
      previousAttemptCount: 'quizzes/previousAttemptCount',
      currentAttempt: 'quizzes/currentAttempt',
      finishedQuiz: 'quizzes/finishedAttempt',
    }),
    questionCount() {
      const { number_of_questions: totalQuestions } = this.currentQuiz
      return totalQuestions
    },
    quizAttempt() {
      return this.currentAttempt
    },
  },
  props: {
    quizId: {
      type: String,
      required: true,
    },
    hasPassedQuiz: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    nextVideo: {
      type: Object,
      required: false,
    },
    videoId: {
      type: String,
      required: true,
    },
  },
  methods: {
    async openModal() {
      this.hasStarted = true
      this.isLoadingModal = true
      this.$emit('isLoadingQuiz', true)
      const { quizId } = this
      await this.$store.dispatch('quizzes/startQuizAttemptByQuizId', quizId)
      this.$emit('isLoadingQuiz', false)
      this.isLoadingModal = false
      this.isModalShown = true
    },
    closeModal() {
      this.$store.dispatch('content/setCurrentQuizId', '')
      this.isModalShown = false
    },
  },
  async created() {
    this.isLoading = true
    this.$emit('isLoadingQuiz', true)
    const { quizId } = this
    await this.$store.dispatch('quizzes/getQuizById', quizId)
    await this.$store.dispatch('quizzes/getQuizAttemptsByQuizId', quizId)
    const body = document.querySelector('body')
    body.classList.add('modal-open')
    this.$emit('isLoadingQuiz', false)
    this.isLoading = false
  },
  destroyed() {
    const body = document.querySelector('body')
    body.classList.remove('modal-open')
    this.$store.dispatch('quizzes/clearQuizStore')
  },
}
</script>

<style lang="scss" scoped>
.quiz {
  margin: 20px 0 0 0;
  &__modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: var(--white);
    z-index: 203;
    overflow-y: auto;
  }
  &__wrapper {
    width: 100%;
    height: 100%;
    max-width: 100vw;
    z-index: 204;
    position: relative;
  }
  &__content-wrapper {
    display: flex;
    flex-wrap: wrap;
    max-width: 1200px;
    min-height: 100%;
    margin: 100px auto 0 auto;
    box-shadow: 6px 75px 50px #0000001a;
    @include tablet {
      min-height: unset;
      padding: 175px 50px;
      margin: 150px auto 0 auto;
      flex-wrap: nowrap;
    }
  }
  &__content-left {
    width: 100%;
    padding: 20px;
    display: flex;
    justify-content: center;
    margin: 0 0 -200px 0;
    @include tablet {
      margin: 0;
      padding: 20px 50px 20px 10px;
      width: 30%;
      border-right: 1px solid var(--border-gray);
    }
  }
  &__rocket {
    width: 200px;
    height: 200px;
    @include tablet {
      width: 290px;
      height: 271px;
    }
  }
  &__content-right {
    width: 100%;
    padding: 20px;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    @include tablet {
      padding: 0 150px 0 50px;
      width: 70%;
      margin: -30px 0 0 0;
      flex-wrap: nowrap;
      justify-content: flex-start;
    }
  }
  &__motivator {
    color: var(--green);
    font-size: 44px;
    text-transform: uppercase;
    font-family: var(--font-medium);
    letter-spacing: 0;
    white-space: nowrap;
  }
  &__count-wrapper {
    display: flex;
    align-items: center;
  }
  &__count-holder {
    width: 65px;
    height: 65px;
    border: 3px solid var(--border-gray);
    border-radius: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin: 0 0 10px 0;
  }
  &__count {
    font-size: 32px;
    font-weight: 700;
    margin: 0 0 0 3px;
  }
  &__count-label {
    font-size: 32px;
    font-weight: 700;
    margin: -10px 0 0 30px;
  }
  &__btn-wrapper {
    display: flex;
    width: 100%;
    margin: -100px 0 0 0;
    justify-content: center;
    @include tablet {
      margin: 15px 0 0 0;
      padding: 0 100px 0 150px;
      align-items: center;
    }
  }
  .btn__large {
    color: var(--black);
    max-height: 50px;
    font-size: 22px;
    &:hover {
      background-color: var(--black);
      color: var(--white);
    }
  }
  &__loader-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
  }
  &__loader {
    width: 5rem;
  }
}
</style>
